import { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import {
  type AuditResult,
  type AuditFinding,
} from "../api/client";
import { prettyFieldLabel } from "../utils/labels";

// ---------------------------------------------------------------------------
// Shared utility
// ---------------------------------------------------------------------------

function fmtDollar(val: number | undefined | null): string {
  if (val == null || val === 0) return "\u2014";
  const neg = val < 0;
  const abs = Math.abs(val);
  const formatted = abs >= 1000
    ? `$${abs.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    : `$${abs.toFixed(2)}`;
  return neg ? `(${formatted})` : formatted;
}

// ---------------------------------------------------------------------------
// AI Audit Results
// ---------------------------------------------------------------------------

interface AuditResultViewProps {
  audit: AuditResult;
}

export default function AuditResultView({ audit }: AuditResultViewProps) {
  const [expanded, setExpanded] = useState(false);
  const findings = audit.findings || [];
  const summary = audit._summary;
  const crossAudit = audit.cross_document_audit;
  const crossFindings = crossAudit?.findings || [];
  const corrections = audit._corrections_applied || [];
  const usage = audit._usage;
  const crossUsage = crossAudit?._usage;

  if (audit.error && findings.length === 0) {
    return (
      <div className="mt-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
        Audit error: {audit.error}
      </div>
    );
  }

  const criticalCount = findings.filter((f: AuditFinding) => f.severity === "critical").length;
  const warningCount = findings.filter((f: AuditFinding) => f.severity === "warning").length;
  const infoCount = findings.filter((f: AuditFinding) => f.severity === "info").length;
  const totalTokens = (usage?.input_tokens || 0) + (usage?.output_tokens || 0)
    + (crossUsage?.input_tokens || 0) + (crossUsage?.output_tokens || 0);

  return (
    <div className="mt-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 w-full text-left"
      >
        {expanded ? <ChevronDown className="w-4 h-4 text-bark-400" /> : <ChevronRight className="w-4 h-4 text-bark-400" />}
        <span className="text-xs font-semibold uppercase tracking-[0.06em] text-bark-400">
          AI Audit
        </span>
        <span className="flex items-center gap-1.5 text-xs">
          {criticalCount > 0 && <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-medium">{criticalCount} critical</span>}
          {warningCount > 0 && <span className="px-1.5 py-0.5 bg-honey-100 text-honey-700 rounded font-medium">{warningCount} warnings</span>}
          {infoCount > 0 && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">{infoCount} info</span>}
          {corrections.length > 0 && <span className="px-1.5 py-0.5 bg-forest-100 text-forest-700 rounded font-medium">{corrections.length} auto-fixed</span>}
        </span>
        {totalTokens > 0 && (
          <span className="ml-auto text-[10px] text-bark-300 tabular-nums">{totalTokens.toLocaleString()} tokens</span>
        )}
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          {/* Summary */}
          {(audit.summary || summary?.summary) && (
            <div className="px-3 py-2 bg-white border border-grove-300 rounded-lg text-xs text-bark-600 leading-relaxed">
              {audit.summary || summary?.summary}
            </div>
          )}

          {/* Findings table */}
          {findings.length > 0 && (
            <div className="bg-white rounded-lg border border-grove-300 overflow-x-auto">
              <table className="w-full data-table text-[12px]">
                <thead>
                  <tr>
                    <th className="w-20">Severity</th>
                    <th className="w-28">Category</th>
                    <th>Description</th>
                    <th className="w-16 num">Conf.</th>
                  </tr>
                </thead>
                <tbody>
                  {findings.map((f: AuditFinding, i: number) => (
                    <tr key={i} className={f.severity === "critical" ? "bg-red-50/50" : f.severity === "warning" ? "bg-honey-50/50" : ""}>
                      <td>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                          f.severity === "critical" ? "bg-red-100 text-red-700"
                          : f.severity === "warning" ? "bg-honey-100 text-honey-700"
                          : "bg-blue-100 text-blue-700"
                        }`}>{f.severity}</span>
                      </td>
                      <td className="text-bark-500">{prettyFieldLabel(f.category || "")}</td>
                      <td className="text-bark-700">
                        {f.tenant_name && <span className="font-medium">{f.tenant_name}: </span>}
                        {f.description}
                        {f.document_value && (
                          <span className="text-bark-400 ml-1">(doc: {f.document_value})</span>
                        )}
                        {f.extracted_value && (
                          <span className="text-bark-400 ml-1">(extracted: {f.extracted_value})</span>
                        )}
                      </td>
                      <td className="num">{f.confidence != null ? `${(f.confidence * 100).toFixed(0)}%` : "\u2014"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Auto-corrections applied */}
          {corrections.length > 0 && (
            <div>
              <h5 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-forest-600 mb-1.5">Auto-Corrections Applied ({corrections.length})</h5>
              <div className="bg-forest-50 border border-forest-200 rounded-lg px-3 py-2 space-y-1">
                {corrections.map((c: Record<string, unknown>, i: number) => (
                  <p key={i} className="text-xs text-forest-700">
                    <span className="font-medium">{prettyFieldLabel((c.type as string) || "")}: </span>
                    {c.tenant_name ? `${c.tenant_name as string} — ` : ""}
                    {c.field ? `${c.field as string}: ` : ""}
                    {c.old_value != null ? String(c.old_value) : ""}
                    {c.new_value != null ? ` → ${String(c.new_value)}` : ""}
                    {c.label ? `${c.label as string}${c.amount != null ? ` (${fmtDollar(c.amount as number)})` : ""}` : ""}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Cross-document audit */}
          {crossFindings.length > 0 && (
            <div>
              <h5 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-bark-400 mb-1.5">Cross-Document Audit ({crossFindings.length} findings)</h5>
              {crossAudit?.summary && (
                <div className="px-3 py-2 mb-2 bg-white border border-grove-300 rounded-lg text-xs text-bark-600 leading-relaxed">
                  {crossAudit.summary}
                </div>
              )}
              <div className="bg-white rounded-lg border border-grove-300 overflow-x-auto">
                <table className="w-full data-table text-[12px]">
                  <thead>
                    <tr><th className="w-20">Severity</th><th className="w-28">Category</th><th>Description</th></tr>
                  </thead>
                  <tbody>
                    {crossFindings.map((f: AuditFinding, i: number) => (
                      <tr key={i} className={f.severity === "critical" ? "bg-red-50/50" : f.severity === "warning" ? "bg-honey-50/50" : ""}>
                        <td>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                            f.severity === "critical" ? "bg-red-100 text-red-700"
                            : f.severity === "warning" ? "bg-honey-100 text-honey-700"
                            : "bg-blue-100 text-blue-700"
                          }`}>{f.severity}</span>
                        </td>
                        <td className="text-bark-500">{prettyFieldLabel(f.category || "")}</td>
                        <td className="text-bark-700">{f.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Token usage */}
          {totalTokens > 0 && (
            <div className="text-[10px] text-bark-300 text-right">
              API usage: {(usage?.input_tokens || 0).toLocaleString()} input + {(usage?.output_tokens || 0).toLocaleString()} output tokens
              {crossUsage && ` (+ ${(crossUsage.input_tokens || 0).toLocaleString()} + ${(crossUsage.output_tokens || 0).toLocaleString()} cross-doc)`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
